// One login for the whole app. The platform-spa client signs the user in
// and the Identity Pool hands back an IAM role chosen from their groups —
// so admins and regular users share a single session and differ only in
// what their role (and the `isAdmin` flag) lets them do. Login/logout are
// full-page redirects, so the snapshot taken at init holds until the next
// navigation re-runs this provider.

import type { Me, PlatformClient } from "@s3p/sdk";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { connect } from "/sdk/index.js";
import { REDIRECT_URI, isPlatformAdmin } from "../lib/sessions-config.ts";
import { SessionExpiredModal } from "./SessionExpiredModal.tsx";

interface SessionState {
  ready: boolean;
  client: PlatformClient | null;
  me: Me | null;
  signedIn: boolean;
  /** The OIDC session aged out and can't silently refresh — the UI should
   *  prompt re-login. Set when the SDK emits `session.expired`. */
  expired: boolean;
  error?: string;
}

export interface SessionValue extends SessionState {
  /** True when the signed-in user holds a platform owner/admin group. */
  isAdmin: boolean;
  /** Navigate to the NATIVE /signin page, returning to `returnTo` after. */
  signIn(returnTo?: string): void;
  /** Sign out of THIS browser. Does not touch agent codes — see
   *  `revokeAgentAccess`. */
  signOut(): void;
  /** Revoke the refresh token, which kills every agent holding this code.
   *  Deliberately separate from `signOut` so it can never happen by accident. */
  revokeAgentAccess(): Promise<void>;
}

const RETURN_TO_KEY = "s3p.returnTo";
const SessionContext = createContext<SessionValue | null>(null);

function msg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SessionState>({
    ready: false,
    client: null,
    me: null,
    signedIn: false,
    expired: false,
  });

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    void (async () => {
      try {
        const client = await connect({ redirectUri: REDIRECT_URI });
        if (cancelled) return;
        // The SDK silently refreshes the session in the background; it only
        // emits `session.expired` when no silent recovery is possible. Flip
        // signedIn off and raise the re-login prompt (SessionExpiredModal).
        unsubscribe = client.events.on("session.expired", () => {
          setState((prev) => ({ ...prev, signedIn: false, expired: true }));
        });
        setState({
          ready: true,
          client,
          me: client.me,
          signedIn: client.signedIn,
          expired: false,
        });
        // Page view — anonymous visitors included (they write with the Identity
        // Pool's unauthenticated role, which may append here and nothing else).
        void client.telemetry.pageView();
      } catch (e) {
        if (!cancelled) {
          setState({
            ready: true,
            client: null,
            me: null,
            signedIn: false,
            expired: false,
            error: msg(e),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      ...state,
      isAdmin: isPlatformAdmin(state.me),
      signIn(returnTo) {
        // Native sign-in page — no hosted UI, no callback URLs. (The hosted
        // redirect remains available via client.signIn() as the future
        // social-IdP escape hatch.)
        const q = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
        window.location.assign(`/signin${q}`);
      },
      signOut() {
        // LOCAL sign-out only — deliberately does NOT pass the refresh token to
        // Cognito's RevokeToken.
        //
        // That refresh token IS the user's agent code (see ConnectAgent). Revoking
        // it here meant that closing your browser session silently killed every
        // agent you had ever handed the code to — they'd start failing with an
        // opaque `token refresh failed (HTTP 400)` hours later, with nothing in
        // the UI connecting the two. "Sign out of this browser" and "de-authorise
        // my agents" are different intents, and only one of them was reachable.
        //
        // Revocation now lives where it belongs: an explicit "Revoke agent access"
        // button in ConnectAgent, which says what it will break.
        void state.client?.auth.signOut().finally(() => {
          window.location.assign("/");
        });
      },
      revokeAgentAccess() {
        const refresh = state.client?.getSession()?.refresh_token;
        return state.client?.auth.signOut(refresh) ?? Promise.resolve();
      },
    }),
    [state],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      <SessionExpiredModal />
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within <SessionProvider>");
  return ctx;
}

/** Consume a stored returnTo after a successful login. */
export function takeReturnTo(): string | null {
  const v = sessionStorage.getItem(RETURN_TO_KEY);
  if (v) sessionStorage.removeItem(RETURN_TO_KEY);
  return v;
}
